import React, { useState, useEffect } from 'react';
import { 
  History, 
  FileText, 
  Download, 
  ChevronDown, 
  ChevronUp,
  Eye,
  Calendar,
  User,
  Tag,
  Link2,
  Plus,
  Edit3,
  Upload,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  BarChart3,
  SpellCheck
} from 'lucide-react';
import axios from 'axios';

const ArticleChangelog = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [exporting, setExporting] = useState(false);
  
  const backendUrl = '';

  useEffect(() => {
    fetchChangelog();
    fetchStats();
  }, [filterType]);

  const fetchChangelog = async () => {
    setLoading(true);
    try {
      const params = filterType !== 'all' ? `?action_type=${filterType}` : '';
      const response = await axios.get(`${backendUrl}/api/admin/changelog/entries${params}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/changelog/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const exportChangelog = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${backendUrl}/api/admin/changelog/export?format=${format}`);
      
      // Create downloadable file
      const data = format === 'csv' ? response.data.data : JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting changelog:', error);
      alert('Failed to export changelog');
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'IMPORT': return <Upload className="text-purple-500" size={18} />;
      case 'EDIT': return <Edit3 className="text-blue-500" size={18} />;
      case 'PUBLISH': return <CheckCircle className="text-green-500" size={18} />;
      case 'UNPUBLISH': return <XCircle className="text-orange-500" size={18} />;
      case 'TEMPLATE_ADDED': return <Plus className="text-pink-500" size={18} />;
      case 'SPELLING_FIXED': return <SpellCheck className="text-yellow-600" size={18} />;
      case 'DELETE': return <Trash2 className="text-red-500" size={18} />;
      default: return <FileText className="text-gray-500" size={18} />;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'IMPORT': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'EDIT': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'PUBLISH': return 'bg-green-100 text-green-700 border-green-200';
      case 'UNPUBLISH': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'TEMPLATE_ADDED': return 'bg-pink-100 text-pink-700 border-pink-200';
      case 'SPELLING_FIXED': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'DELETE': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpanded = (entryId) => {
    setExpandedEntry(expandedEntry === entryId ? null : entryId);
  };

  return (
    <div className="space-y-6">
      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <BarChart3 className="mx-auto text-purple-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.total_entries}</p>
            <p className="text-xs text-gray-500">Total Entries</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <Upload className="mx-auto text-purple-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.imports_count}</p>
            <p className="text-xs text-gray-500">Imports</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <Edit3 className="mx-auto text-blue-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.edits_count}</p>
            <p className="text-xs text-gray-500">Edits</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.publishes_count}</p>
            <p className="text-xs text-gray-500">Published</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <Plus className="mx-auto text-pink-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.templates_added_count}</p>
            <p className="text-xs text-gray-500">Templates Added</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center">
            <FileText className="mx-auto text-orange-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-gray-900">{stats.articles_tracked}</p>
            <p className="text-xs text-gray-500">Articles Tracked</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <History className="text-purple-500" size={24} />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Article Changelog</h3>
              <p className="text-sm text-gray-500">Track all content changes for accountability</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Filter */}
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="changelog-filter"
              >
                <option value="all">All Actions</option>
                <option value="IMPORT">Imports</option>
                <option value="EDIT">Edits</option>
                <option value="PUBLISH">Published</option>
                <option value="UNPUBLISH">Unpublished</option>
                <option value="TEMPLATE_ADDED">Templates Added</option>
                <option value="SPELLING_FIXED">Spelling Fixes</option>
              </select>
            </div>
            
            {/* Refresh */}
            <button
              onClick={() => { fetchChangelog(); fetchStats(); }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-500" />
            </button>
            
            {/* Export Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => exportChangelog('json')}
                disabled={exporting}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                data-testid="export-json-btn"
              >
                <Download size={16} />
                <span>JSON</span>
              </button>
              <button
                onClick={() => exportChangelog('csv')}
                disabled={exporting}
                className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                data-testid="export-csv-btn"
              >
                <Download size={16} />
                <span>CSV</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Changelog Entries */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="animate-spin mx-auto text-purple-500 mb-4" size={32} />
            <p className="text-gray-500">Loading changelog...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <History className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Changelog Entries Yet</h3>
            <p className="text-gray-500">
              Changes will be logged here when you import, edit, or publish articles.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <div key={entry.id} className="hover:bg-gray-50 transition-colors">
                {/* Entry Header */}
                <button
                  onClick={() => toggleExpanded(entry.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                  data-testid={`changelog-entry-${entry.id}`}
                >
                  <div className="flex items-center space-x-4">
                    {getActionIcon(entry.action_type)}
                    <div>
                      <div className="flex items-center space-x-3">
                        <h4 className="font-semibold text-gray-900">{entry.article_title}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${getActionColor(entry.action_type)}`}>
                          {entry.action_type}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Calendar size={12} />
                          <span>{formatDate(entry.timestamp)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <User size={12} />
                          <span>{entry.user}</span>
                        </span>
                        {entry.source_url && (
                          <span className="flex items-center space-x-1">
                            <Link2 size={12} />
                            <span>External Source</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {entry.changes_summary.length} change{entry.changes_summary.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {expandedEntry === entry.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                {expandedEntry === entry.id && (
                  <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                    <div className="grid md:grid-cols-2 gap-6 mt-4">
                      {/* Changes Summary */}
                      <div>
                        <h5 className="font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                          <Edit3 size={16} />
                          <span>Changes Made</span>
                        </h5>
                        <ul className="space-y-2">
                          {entry.changes_summary.map((change, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm">
                              <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{change}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Template Sections Added */}
                      {entry.template_sections_added && entry.template_sections_added.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                            <Plus size={16} />
                            <span>Template Sections Added</span>
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {entry.template_sections_added.map((section, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                              >
                                {section}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      {/* Spelling resolution recap */}
                      {entry.metadata && entry.metadata.by_action && (
                        <div className="md:col-span-2">
                          <h5 className="font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                            <SpellCheck size={16} />
                            <span>Spelling Recap — {entry.metadata.resolved_total || 0} resolved</span>
                          </h5>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {(() => {
                              const meta = {
                                split: { label: 'Missing-space split', cls: 'bg-blue-100 text-blue-700' },
                                manual: { label: 'Manual fix', cls: 'bg-yellow-100 text-yellow-700' },
                                suggestion: { label: 'Suggestion applied', cls: 'bg-green-100 text-green-700' },
                                brand_dict: { label: 'Added to Brand Dictionary', cls: 'bg-purple-100 text-purple-700' },
                                ignored: { label: 'Ignored', cls: 'bg-gray-100 text-gray-600' },
                              };
                              return Object.keys(meta)
                                .filter(k => entry.metadata.by_action[k])
                                .map(k => (
                                  <span key={k} className={`text-xs px-2.5 py-1 rounded-full font-medium ${meta[k].cls}`}>
                                    {meta[k].label}: {entry.metadata.by_action[k]}
                                  </span>
                                ));
                            })()}
                          </div>
                          {Array.isArray(entry.metadata.details) && entry.metadata.details.length > 0 && (
                            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-56 overflow-y-auto">
                              {entry.metadata.details.map((d, i) => (
                                <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                                  <span className="font-medium text-gray-800">{d.word}</span>
                                  <span className="text-gray-500">
                                    {d.action === 'ignored' ? 'ignored'
                                      : d.action === 'brand_dict' ? '→ Brand Dictionary'
                                      : d.to ? `→ "${d.to}"` : d.action}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {entry.metadata && Object.keys(entry.metadata).length > 0 && !entry.metadata.by_action && (
                        <div className="md:col-span-2">
                          <h5 className="font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                            <BarChart3 size={16} />
                            <span>Quality Metrics</span>
                          </h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {entry.metadata.word_count_after && (
                              <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <p className="text-lg font-bold text-gray-900">{entry.metadata.word_count_after}</p>
                                <p className="text-xs text-gray-500">Words</p>
                              </div>
                            )}
                            {entry.metadata.spelling_score && (
                              <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <p className="text-lg font-bold text-gray-900">{entry.metadata.spelling_score}%</p>
                                <p className="text-xs text-gray-500">Spelling Score</p>
                              </div>
                            )}
                            {entry.metadata.completeness_score && (
                              <div className="bg-white p-3 rounded-lg border border-gray-200">
                                <p className="text-lg font-bold text-gray-900">{entry.metadata.completeness_score}%</p>
                                <p className="text-xs text-gray-500">Completeness</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Source URL */}
                      {entry.source_url && (
                        <div className="md:col-span-2">
                          <h5 className="font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                            <Link2 size={16} />
                            <span>Source URL</span>
                          </h5>
                          <a
                            href={entry.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-600 hover:text-purple-800 break-all"
                          >
                            {entry.source_url}
                          </a>
                        </div>
                      )}

                      {/* Article ID */}
                      <div className="md:col-span-2 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-400">
                          Entry ID: {entry.id} | Article ID: {entry.article_id}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleChangelog;
